import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation, Trans } from "react-i18next";
import { getTenants, createTenant, updateTenant, deleteTenant, getTenantUsers, createTenantUser, updateTenantUser, deleteTenantUser, getTenantEdges, getTenantLocations, createTenantLocation, updateTenantLocation, deleteTenantLocation } from "../api/tenantApi";
import { toast } from "react-toastify";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Switch,
  Tabs,
  Tab,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import TenantCapabilities from "../components/TenantCapabilities";
import TenantProducts, { PRODUCT_CHOICES } from "../components/TenantProducts";

const TABS = [
  { key: "general" },
  { key: "subtenants" },
  { key: "locations" },
  { key: "users" },
  { key: "capabilities", superadminOnly: true },
];

// Backend role value -> locale key (the stored value "operatör" stays as is, only the display is translated).
const roleKey = (role) => (role === "operatör" ? "operator" : role);

// Tek bir tenant için sekmeli, düz (nested accordion içermeyen) çalışma alanı.
// key={currentTenant.id} ile monte edilir, böylece tenant değiştiğinde tüm
// local state (aktif sekme, fetch edilmiş listeler) sıfırlanır.
function TenantWorkspace({ tenantPath, setTenantPath, accessToken, me, tenantDetails, onTenantsFetched, onExitToRoot }) {
  const { t } = useTranslation();
  const roleLabel = (role) => (role ? t(`admin.tenants.roles.${roleKey(role)}`, { defaultValue: role }) : "-");
  const currentTenant = tenantPath[tenantPath.length - 1];
  const detail = tenantDetails[currentTenant.id] || currentTenant;
  const parentEntry = tenantPath.length > 1 ? tenantPath[tenantPath.length - 2] : null;

  const [activeTab, setActiveTab] = useState("general");
  const [capsKey, setCapsKey] = useState(0); // remounts the capability list after the product profile changed it
  const canManage = me?.global_role === "superadmin" || me?.tenant_role === "admin";

  // ---- Genel Bilgiler: tenant düzenle/sil (hem currentTenant hem Alt
  // Müşteriler sekmesindeki bir satır için aynı dialog kullanılır) ----
  const [editTarget, setEditTarget] = useState(null);
  const [showEditTenantModal, setShowEditTenantModal] = useState(false);
  const [submittingTenant, setSubmittingTenant] = useState(false);
  const [editTenantForm, setEditTenantForm] = useState({ name: "", contact_person: "", rca_enabled: false, max_vpn_users: "", security_notification_emails: "" });

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showDeleteTenantDialog, setShowDeleteTenantDialog] = useState(false);
  const [deletingTenant, setDeletingTenant] = useState(false);

  const openEditTenant = (target) => {
    setEditTarget(target);
    setEditTenantForm({
      name: target.name || "",
      contact_person: target.contact_person || "",
      rca_enabled: target.rca_enabled || false,
      max_vpn_users: target.max_vpn_users ?? "",
      security_notification_emails: (target.security_notification_emails || []).join(", "),
    });
    setShowEditTenantModal(true);
  };

  const handleUpdateTenant = async () => {
    if (!editTenantForm.name.trim()) {
      toast.error(t("admin.tenants.edit.nameEmpty"));
      return;
    }
    setSubmittingTenant(true);
    try {
      const payload = {
        ...editTenantForm,
        max_vpn_users: editTenantForm.max_vpn_users === "" ? null : Number(editTenantForm.max_vpn_users),
        security_notification_emails: editTenantForm.security_notification_emails.split(/[,;\n]/).map((email) => email.trim()).filter(Boolean),
      };
      await updateTenant(accessToken, editTarget.id, payload);
      toast.success(t("admin.tenants.edit.updated"));
      setShowEditTenantModal(false);
      onTenantsFetched([{ ...editTarget, ...payload }]);
      if (editTarget.id === currentTenant.id) {
        setTenantPath((prev) => prev.map((p, i) => (i === prev.length - 1 ? { ...p, name: payload.name } : p)));
      } else {
        fetchSubTenants();
      }
    } catch (err) {
      console.error("Tenant güncellenemedi:", err);
      toast.error(err?.response?.data?.detail || t("admin.tenants.edit.updateFailed"));
    } finally {
      setSubmittingTenant(false);
    }
  };

  const handleConfirmDeleteTenant = async () => {
    setDeletingTenant(true);
    try {
      const result = await deleteTenant(accessToken, deleteTarget.id);
      const deletedEdges = result.deleted_edges || 0;
      const deletedUsers = result.deleted_users || 0;
      if (deletedEdges > 0 || deletedUsers > 0) {
        toast.success(t("admin.tenants.del.doneWithCounts", { name: deleteTarget.name, users: deletedUsers, edges: deletedEdges }));
      } else {
        toast.success(t("admin.tenants.del.done", { name: deleteTarget.name }));
      }
      setShowDeleteTenantDialog(false);
      if (deleteTarget.id === currentTenant.id) {
        if (tenantPath.length <= 1) {
          onExitToRoot();
        } else {
          setTenantPath((prev) => prev.slice(0, -1));
        }
      } else {
        fetchSubTenants();
      }
    } catch (err) {
      console.error("Tenant silinemedi:", err);
      toast.error(err?.response?.data?.detail || t("admin.tenants.del.failed"));
    } finally {
      setDeletingTenant(false);
    }
  };

  // ---- Alt Müşteriler ----
  const [subTenants, setSubTenants] = useState([]);
  const [subTenantsFetched, setSubTenantsFetched] = useState(false);
  const [loadingSubTenants, setLoadingSubTenants] = useState(false);
  const [showAddSubTenantModal, setShowAddSubTenantModal] = useState(false);
  const [submittingSubTenant, setSubmittingSubTenant] = useState(false);
  const [subTenantForm, setSubTenantForm] = useState({ name: "", contact_person: "" });

  const fetchSubTenants = async () => {
    if (!accessToken) return;
    setLoadingSubTenants(true);
    try {
      const data = await getTenants(accessToken, { parent_tenant_id: currentTenant.id, limit: 100 });
      const items = data.items || data || [];
      setSubTenants(items);
      setSubTenantsFetched(true);
      onTenantsFetched(items);
    } catch (err) {
      console.error("Alt tenant'lar alınamadı:", err);
    } finally {
      setLoadingSubTenants(false);
    }
  };

  const handleCreateSubTenant = async () => {
    if (!subTenantForm.name.trim()) {
      toast.error(t("admin.tenants.sub.nameEmpty"));
      return;
    }
    setSubmittingSubTenant(true);
    try {
      await createTenant(accessToken, { ...subTenantForm, parent_tenant_id: currentTenant.id });
      toast.success(t("admin.tenants.sub.created"));
      setShowAddSubTenantModal(false);
      setSubTenantForm({ name: "", contact_person: "" });
      fetchSubTenants();
    } catch (err) {
      console.error("Alt tenant oluşturulamadı:", err);
      toast.error(err?.response?.data?.detail || t("admin.tenants.sub.createFailed"));
    } finally {
      setSubmittingSubTenant(false);
    }
  };

  const enterTenant = (target) => {
    setTenantPath((prev) => [...prev, { id: target.id, name: target.name }]);
  };

  // ---- Kullanıcılar ----
  const [users, setUsers] = useState([]);
  const [usersFetched, setUsersFetched] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [submittingUser, setSubmittingUser] = useState(false);
  const [userForm, setUserForm] = useState({ email: "", full_name: "", password: "", tenant_role: "operatör" });
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editUserForm, setEditUserForm] = useState({ full_name: "", email: "", tenant_role: "", password: "" });
  const [showDeleteUserDialog, setShowDeleteUserDialog] = useState(false);
  const [deletingUser, setDeletingUser] = useState(null);
  const [deletingUserLoading, setDeletingUserLoading] = useState(false);

  const fetchUsers = async () => {
    if (!accessToken) return;
    setLoadingUsers(true);
    try {
      const data = await getTenantUsers(accessToken, currentTenant.id);
      setUsers(data.items || data || []);
      setUsersFetched(true);
    } catch (err) {
      console.error("Kullanıcılar alınamadı:", err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const resetUserForm = () => setUserForm({ email: "", full_name: "", password: "", tenant_role: "operatör" });

  const handleCreateUser = async () => {
    if (!userForm.email.trim()) { toast.error(t("admin.tenants.users.emailEmpty")); return; }
    if (!userForm.full_name.trim()) { toast.error(t("admin.tenants.users.fullNameEmpty")); return; }
    if (!userForm.password.trim()) { toast.error(t("admin.tenants.users.passwordEmpty")); return; }
    setSubmittingUser(true);
    try {
      await createTenantUser(accessToken, currentTenant.id, userForm);
      toast.success(t("admin.tenants.users.added"));
      setShowUserModal(false);
      resetUserForm();
      fetchUsers();
    } catch (err) {
      console.error("Kullanıcı eklenemedi:", err);
      toast.error(err?.response?.data?.detail || t("admin.tenants.users.addFailed"));
    } finally {
      setSubmittingUser(false);
    }
  };

  const handleOpenEditUser = (user) => {
    setEditingUser(user);
    setEditUserForm({ full_name: user.full_name || "", email: user.email || "", tenant_role: user.tenant_role || "operatör", password: "" });
    setShowEditUserModal(true);
  };

  const handleUpdateUser = async () => {
    if (!editUserForm.email.trim()) { toast.error(t("admin.tenants.users.emailEmpty")); return; }
    if (!editUserForm.full_name.trim()) { toast.error(t("admin.tenants.users.fullNameEmpty")); return; }
    setSubmittingUser(true);
    try {
      const payload = { full_name: editUserForm.full_name, email: editUserForm.email, tenant_role: editUserForm.tenant_role };
      if (editUserForm.password.trim()) payload.password = editUserForm.password;
      await updateTenantUser(accessToken, currentTenant.id, editingUser.id, payload);
      toast.success(t("admin.tenants.users.updated"));
      setShowEditUserModal(false);
      setEditingUser(null);
      fetchUsers();
    } catch (err) {
      console.error("Kullanıcı güncellenemedi:", err);
      toast.error(err?.response?.data?.detail || t("admin.tenants.users.updateFailed"));
    } finally {
      setSubmittingUser(false);
    }
  };

  const handleOpenDeleteUser = (user) => { setDeletingUser(user); setShowDeleteUserDialog(true); };

  const handleConfirmDeleteUser = async () => {
    if (!deletingUser) return;
    setDeletingUserLoading(true);
    try {
      await deleteTenantUser(accessToken, currentTenant.id, deletingUser.id);
      toast.success(t("admin.tenants.users.deleted"));
      setShowDeleteUserDialog(false);
      setDeletingUser(null);
      fetchUsers();
    } catch (err) {
      console.error("Kullanıcı silinemedi:", err);
      toast.error(err?.response?.data?.detail || t("admin.tenants.users.deleteFailed"));
    } finally {
      setDeletingUserLoading(false);
    }
  };

  // ---- Lokasyonlar ----
  const [locations, setLocations] = useState([]);
  const [locationsFetched, setLocationsFetched] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [submittingLocation, setSubmittingLocation] = useState(false);
  const [locationForm, setLocationForm] = useState({ name: "", address: "" });
  const [showEditLocationModal, setShowEditLocationModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [editLocationForm, setEditLocationForm] = useState({ name: "", address: "" });
  const [showDeleteLocationDialog, setShowDeleteLocationDialog] = useState(false);
  const [deletingLocation, setDeletingLocation] = useState(null);
  const [deletingLocationLoading, setDeletingLocationLoading] = useState(false);
  const [locationEdges, setLocationEdges] = useState({});

  const fetchLocationEdgeCount = async (locationId) => {
    if (!accessToken) return;
    try {
      const data = await getTenantEdges(accessToken, currentTenant.id, { location_id: locationId, limit: 100 });
      setLocationEdges((prev) => ({ ...prev, [locationId]: data.items || [] }));
    } catch (err) {
      console.error(`Lokasyon ${locationId} edge sayısı alınamadı:`, err);
    }
  };

  const fetchLocations = async () => {
    if (!accessToken) return;
    setLoadingLocations(true);
    try {
      const data = await getTenantLocations(accessToken, currentTenant.id);
      const items = data.items || data || [];
      setLocations(items);
      setLocationsFetched(true);
      items.forEach((loc) => fetchLocationEdgeCount(loc.id));
    } catch (err) {
      console.error("Lokasyonlar alınamadı:", err);
    } finally {
      setLoadingLocations(false);
    }
  };

  const resetLocationForm = () => setLocationForm({ name: "", address: "" });

  const handleCreateLocation = async () => {
    if (!locationForm.name.trim()) { toast.error(t("admin.tenants.loc.nameEmpty")); return; }
    setSubmittingLocation(true);
    try {
      await createTenantLocation(accessToken, currentTenant.id, locationForm);
      toast.success(t("admin.tenants.loc.added"));
      setShowLocationModal(false);
      resetLocationForm();
      fetchLocations();
    } catch (err) {
      console.error("Lokasyon eklenemedi:", err);
      toast.error(err?.response?.data?.detail || t("admin.tenants.loc.addFailed"));
    } finally {
      setSubmittingLocation(false);
    }
  };

  const handleOpenEditLocation = (location) => {
    setEditingLocation(location);
    setEditLocationForm({ name: location.name || "", address: location.address || "" });
    setShowEditLocationModal(true);
  };

  const handleUpdateLocation = async () => {
    if (!editLocationForm.name.trim()) { toast.error(t("admin.tenants.loc.nameEmpty")); return; }
    setSubmittingLocation(true);
    try {
      await updateTenantLocation(accessToken, currentTenant.id, editingLocation.id, editLocationForm);
      toast.success(t("admin.tenants.loc.updated"));
      setShowEditLocationModal(false);
      setEditingLocation(null);
      fetchLocations();
    } catch (err) {
      console.error("Lokasyon güncellenemedi:", err);
      toast.error(err?.response?.data?.detail || t("admin.tenants.loc.updateFailed"));
    } finally {
      setSubmittingLocation(false);
    }
  };

  const handleOpenDeleteLocation = (location) => { setDeletingLocation(location); setShowDeleteLocationDialog(true); };

  const handleConfirmDeleteLocation = async () => {
    if (!deletingLocation) return;
    setDeletingLocationLoading(true);
    try {
      await deleteTenantLocation(accessToken, currentTenant.id, deletingLocation.id);
      toast.success(t("admin.tenants.loc.deleted"));
      setShowDeleteLocationDialog(false);
      setDeletingLocation(null);
      fetchLocations();
    } catch (err) {
      console.error("Lokasyon silinemedi:", err);
      toast.error(err?.response?.data?.detail || t("admin.tenants.loc.deleteFailed"));
    } finally {
      setDeletingLocationLoading(false);
    }
  };

  // Sekme ilk kez seçildiğinde ilgili listeyi lazy fetch et
  useEffect(() => {
    if (activeTab === "subtenants" && !subTenantsFetched) fetchSubTenants();
    if (activeTab === "locations" && !locationsFetched) fetchLocations();
    if (activeTab === "users" && !usersFetched) fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  return (
    <Box sx={{ color: "var(--text-primary)" }}>
      {/* Breadcrumb */}
      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.5, mb: 1 }}>
        <Typography
          component="span"
          onClick={onExitToRoot}
          sx={{ fontSize: 13, color: "var(--text-muted)", cursor: me?.global_role === "superadmin" ? "pointer" : "default", "&:hover": me?.global_role === "superadmin" ? { color: "var(--accent-navy-light)" } : {} }}
        >
          {me?.global_role === "superadmin" ? t("admin.tenants.rootCrumb") : t("admin.tenants.manageCrumb")}
        </Typography>
        {tenantPath.map((p, i) => (
          <React.Fragment key={p.id}>
            <Typography component="span" sx={{ fontSize: 13, color: "var(--text-muted)" }}>›</Typography>
            <Typography
              component="span"
              onClick={() => setTenantPath(tenantPath.slice(0, i + 1))}
              sx={{
                fontSize: 13,
                fontWeight: i === tenantPath.length - 1 ? 600 : 400,
                color: i === tenantPath.length - 1 ? "var(--text-primary)" : "var(--text-muted)",
                cursor: i === tenantPath.length - 1 ? "default" : "pointer",
                "&:hover": i === tenantPath.length - 1 ? {} : { color: "var(--accent-navy-light)" },
              }}
            >
              {p.name}
            </Typography>
          </React.Fragment>
        ))}
      </Box>

      <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>{currentTenant.name}</Typography>

      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{ borderBottom: "1px solid var(--border)", mb: 2, minHeight: 40 }}
      >
        {TABS.filter((tab) => !tab.superadminOnly || me?.global_role === "superadmin").map((tab) => (
          <Tab key={tab.key} value={tab.key} label={t(`admin.tenants.tabs.${tab.key}`)} sx={{ minHeight: 40, textTransform: "none" }} />
        ))}
      </Tabs>

      {activeTab === "capabilities" && me?.global_role === "superadmin" && (
        <>
          <TenantProducts tenantId={currentTenant.id} accessToken={accessToken} onSaved={() => setCapsKey((k) => k + 1)} />
          <TenantCapabilities key={capsKey} tenantId={currentTenant.id} accessToken={accessToken} />
        </>
      )}

      {/* Genel Bilgiler */}
      {activeTab === "general" && (
        <Box sx={{ backgroundColor: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: "8px", p: 3, maxWidth: 480 }}>
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" sx={{ color: "var(--text-muted)" }}>{t("admin.tenants.general.name")}</Typography>
            <Typography>{detail.name}</Typography>
          </Box>
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" sx={{ color: "var(--text-muted)" }}>{t("admin.tenants.general.contact")}</Typography>
            <Typography>{detail.contact_person || "-"}</Typography>
          </Box>
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" sx={{ color: "var(--text-muted)" }}>{t("admin.tenants.general.parent")}</Typography>
            <Typography>{parentEntry ? parentEntry.name : t("admin.tenants.general.rootTenant")}</Typography>
          </Box>
          {detail.rca_enabled && (
            <Box component="span" sx={{ display: "inline-block", px: 1, py: 0.25, borderRadius: "10px", fontSize: 11, fontWeight: 600, color: "#3fb950", backgroundColor: "rgba(63,185,80,0.12)", mb: 1.5 }}>
              RCA ✓
            </Box>
          )}
          <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
            <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => openEditTenant(detail)} sx={{ color: "var(--accent-navy-light)", borderColor: "var(--border)" }}>
              {t("admin.tenants.editBtn")}
            </Button>
            <Button size="small" variant="outlined" startIcon={<DeleteIcon />} onClick={() => { setDeleteTarget(detail); setShowDeleteTenantDialog(true); }} sx={{ color: "var(--danger)", borderColor: "var(--border)" }}>
              {t("admin.tenants.deleteBtn")}
            </Button>
          </Box>
        </Box>
      )}

      {/* Alt Müşteriler */}
      {activeTab === "subtenants" && (
        <Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1.5 }}>
            {canManage && (
              <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setShowAddSubTenantModal(true)} sx={{ color: "var(--accent-navy-light)", borderColor: "var(--border)" }}>
                {t("admin.tenants.sub.add")}
              </Button>
            )}
          </Box>
          <TableContainer component={Paper} sx={{ backgroundColor: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: "8px" }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "var(--bg-hover)" }}>
                  <TableCell sx={{ color: "var(--text-muted)", fontWeight: 600 }}>{t("admin.tenants.sub.company")}</TableCell>
                  <TableCell sx={{ color: "var(--text-muted)", fontWeight: 600 }}>{t("admin.tenants.sub.contact")}</TableCell>
                  <TableCell sx={{ color: "var(--text-muted)", fontWeight: 600 }} align="center">{t("admin.tenants.sub.actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingSubTenants ? (
                  <TableRow><TableCell colSpan={3} sx={{ textAlign: "center", py: 3 }}><CircularProgress size={22} /></TableCell></TableRow>
                ) : subTenants.length === 0 ? (
                  <TableRow><TableCell colSpan={3} sx={{ color: "var(--text-muted)", textAlign: "center", py: 3 }}>{t("admin.tenants.sub.empty")}</TableCell></TableRow>
                ) : (
                  subTenants.map((sub) => (
                    <TableRow key={sub.id} sx={{ "&:hover": { backgroundColor: "var(--bg-hover)" } }}>
                      <TableCell sx={{ fontWeight: 600 }}>{sub.name}</TableCell>
                      <TableCell>{sub.contact_person || "-"}</TableCell>
                      <TableCell align="center">
                        <Button size="small" variant="outlined" onClick={() => enterTenant(sub)} sx={{ color: "var(--accent-navy-light)", borderColor: "var(--border)", mr: 1 }}>
                          {t("admin.tenants.sub.manage")}
                        </Button>
                        <IconButton size="small" sx={{ color: "var(--accent-navy-light)" }} title={t("admin.tenants.editBtn")} onClick={() => openEditTenant(sub)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" sx={{ color: "var(--danger)" }} title={t("admin.tenants.deleteBtn")} onClick={() => { setDeleteTarget(sub); setShowDeleteTenantDialog(true); }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Lokasyonlar */}
      {activeTab === "locations" && (
        <Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
            <Typography variant="body2" sx={{ color: "var(--accent-navy-light)", backgroundColor: "rgba(88,166,255,0.1)", borderRadius: 1, px: 1.5, py: 1, fontSize: 13 }}>
              {t("admin.tenants.loc.hint")}
            </Typography>
            {canManage && (
              <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setShowLocationModal(true)} sx={{ color: "#a371f7", borderColor: "var(--border)", ml: 2, whiteSpace: "nowrap" }}>
                {t("admin.tenants.loc.add")}
              </Button>
            )}
          </Box>
          <TableContainer component={Paper} sx={{ backgroundColor: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: "8px" }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "var(--bg-hover)" }}>
                  <TableCell sx={{ color: "var(--text-muted)", fontWeight: 600 }}>{t("admin.tenants.loc.name")}</TableCell>
                  <TableCell sx={{ color: "var(--text-muted)", fontWeight: 600 }}>{t("admin.tenants.loc.address")}</TableCell>
                  <TableCell sx={{ color: "var(--text-muted)", fontWeight: 600 }}>{t("admin.tenants.loc.deviceCount")}</TableCell>
                  <TableCell sx={{ color: "var(--text-muted)", fontWeight: 600 }} align="center">{t("admin.tenants.sub.actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingLocations ? (
                  <TableRow><TableCell colSpan={4} sx={{ textAlign: "center", py: 3 }}><CircularProgress size={22} /></TableCell></TableRow>
                ) : locations.length === 0 ? (
                  <TableRow><TableCell colSpan={4} sx={{ color: "var(--text-muted)", textAlign: "center", py: 3 }}>{t("admin.tenants.loc.empty")}</TableCell></TableRow>
                ) : (
                  locations.map((location) => (
                    <TableRow key={location.id} sx={{ "&:hover": { backgroundColor: "var(--bg-hover)" } }}>
                      <TableCell sx={{ fontWeight: 500 }}>📍 {location.name}</TableCell>
                      <TableCell>{location.address || "-"}</TableCell>
                      <TableCell sx={{ color: "var(--text-muted)", fontSize: 12 }}>
                        {locationEdges[location.id] ? (
                          <Box component="span" sx={{ px: 1, py: 0.25, backgroundColor: "var(--border)", borderRadius: "8px", fontSize: 11 }}>
                            {t("admin.tenants.loc.devices", { count: locationEdges[location.id].length })}
                          </Box>
                        ) : "..."}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton size="small" sx={{ color: "var(--accent-navy-light)" }} title={t("admin.tenants.editBtn")} onClick={() => handleOpenEditLocation(location)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" sx={{ color: "var(--danger)", ml: 0.5 }} title={t("admin.tenants.deleteBtn")} onClick={() => handleOpenDeleteLocation(location)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Kullanıcılar */}
      {activeTab === "users" && (
        <Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1.5 }}>
            {canManage && (
              <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setShowUserModal(true)} sx={{ color: "var(--accent-navy-light)", borderColor: "var(--border)" }}>
                {t("admin.tenants.users.add")}
              </Button>
            )}
          </Box>
          <TableContainer component={Paper} sx={{ backgroundColor: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: "8px" }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "var(--bg-hover)" }}>
                  <TableCell sx={{ color: "var(--text-muted)", fontWeight: 600 }}>{t("admin.tenants.users.fullName")}</TableCell>
                  <TableCell sx={{ color: "var(--text-muted)", fontWeight: 600 }}>{t("admin.tenants.users.email")}</TableCell>
                  <TableCell sx={{ color: "var(--text-muted)", fontWeight: 600 }}>{t("admin.tenants.users.role")}</TableCell>
                  <TableCell sx={{ color: "var(--text-muted)", fontWeight: 600 }} align="center">{t("admin.tenants.sub.actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingUsers ? (
                  <TableRow><TableCell colSpan={4} sx={{ textAlign: "center", py: 3 }}><CircularProgress size={22} /></TableCell></TableRow>
                ) : users.length === 0 ? (
                  <TableRow><TableCell colSpan={4} sx={{ color: "var(--text-muted)", textAlign: "center", py: 3 }}>{t("admin.tenants.users.empty")}</TableCell></TableRow>
                ) : (
                  users.map((user) => (
                    <TableRow key={user.id} sx={{ "&:hover": { backgroundColor: "var(--bg-hover)" } }}>
                      <TableCell>{user.full_name || "-"}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{roleLabel(user.tenant_role || user.global_role)}</TableCell>
                      <TableCell align="center">
                        <IconButton size="small" sx={{ color: "var(--accent-navy-light)" }} title={t("admin.tenants.editBtn")} onClick={() => handleOpenEditUser(user)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" sx={{ color: "var(--danger)", ml: 0.5 }} title={t("admin.tenants.deleteBtn")} onClick={() => handleOpenDeleteUser(user)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* ---- Dialoglar ---- */}

      {/* Alt Müşteri Ekleme */}
      <Dialog open={showAddSubTenantModal} onClose={() => { setShowAddSubTenantModal(false); setSubTenantForm({ name: "", contact_person: "" }); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
          {t("admin.tenants.sub.addTitle", { name: currentTenant.name })}
          <IconButton size="small" onClick={() => setShowAddSubTenantModal(false)} sx={{ color: "#666" }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField autoFocus margin="dense" label={t("admin.tenants.sub.companyLabel")} fullWidth variant="outlined" value={subTenantForm.name} onChange={(e) => setSubTenantForm({ ...subTenantForm, name: e.target.value })} sx={{ mb: 2, mt: 1 }} />
          <TextField margin="dense" label={t("admin.tenants.sub.contact")} fullWidth variant="outlined" value={subTenantForm.contact_person} onChange={(e) => setSubTenantForm({ ...subTenantForm, contact_person: e.target.value })} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" onClick={handleCreateSubTenant} disabled={submittingSubTenant} sx={{ backgroundColor: "var(--success)", "&:hover": { backgroundColor: "#2ea043" } }}>
            {submittingSubTenant ? t("admin.tenants.saving") : t("admin.tenants.save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tenant Düzenleme (currentTenant ya da bir alt müşteri satırı) */}
      <Dialog open={showEditTenantModal} onClose={() => setShowEditTenantModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {t("admin.tenants.edit.title")}
          <IconButton size="small" onClick={() => setShowEditTenantModal(false)} sx={{ color: "#666" }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField autoFocus margin="dense" label={t("admin.tenants.edit.nameLabel")} fullWidth variant="outlined" value={editTenantForm.name} onChange={(e) => setEditTenantForm({ ...editTenantForm, name: e.target.value })} sx={{ mb: 2, mt: 1 }} />
          <TextField margin="dense" label={t("admin.tenants.sub.contact")} fullWidth variant="outlined" value={editTenantForm.contact_person} onChange={(e) => setEditTenantForm({ ...editTenantForm, contact_person: e.target.value })} sx={{ mb: 1 }} />
          {me?.global_role === "superadmin" && (
            <>
              <FormControlLabel
                control={<Switch checked={editTenantForm.rca_enabled} onChange={(e) => setEditTenantForm({ ...editTenantForm, rca_enabled: e.target.checked })} color="success" />}
                label={t("admin.tenants.edit.rca")}
                sx={{ display: "block", mb: 1.5 }}
              />
              <TextField
                margin="dense"
                label={t("admin.tenants.edit.vpnLimit")}
                type="number"
                fullWidth
                variant="outlined"
                value={editTenantForm.max_vpn_users}
                onChange={(e) => setEditTenantForm({ ...editTenantForm, max_vpn_users: e.target.value })}
              />
              <TextField
                margin="dense"
                label={t("admin.tenants.edit.securityEmails")}
                fullWidth
                multiline
                value={editTenantForm.security_notification_emails}
                onChange={(e) => setEditTenantForm({ ...editTenantForm, security_notification_emails: e.target.value })}
                helperText={t("admin.tenants.edit.securityEmailsHelp")}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" onClick={handleUpdateTenant} disabled={submittingTenant} sx={{ backgroundColor: "var(--success)", "&:hover": { backgroundColor: "#2ea043" } }}>
            {submittingTenant ? t("admin.tenants.saving") : t("admin.tenants.save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tenant Silme */}
      <Dialog open={showDeleteTenantDialog} onClose={() => !deletingTenant && setShowDeleteTenantDialog(false)} maxWidth="xs" fullWidth
        slotProps={{ paper: { sx: { backgroundColor: "var(--bg-card)", border: "1px solid var(--danger)", borderRadius: "8px" } } }}
      >
        <DialogTitle sx={{ color: "var(--danger)", fontWeight: 600, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}><DeleteIcon />{t("admin.tenants.del.title")}</Box>
          <IconButton size="small" onClick={() => setShowDeleteTenantDialog(false)} disabled={deletingTenant} sx={{ color: "var(--text-muted)" }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "var(--text-primary)", mb: 2 }}>
            <Trans i18nKey="admin.tenants.del.question" values={{ name: deleteTarget?.name }} components={{ b: <strong /> }} />
          </Typography>
          <Typography sx={{ color: "var(--danger)", fontSize: 14, backgroundColor: "rgba(248,81,73,0.1)", p: 1.5, borderRadius: 1 }}>
            {t("admin.tenants.del.warning")}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" onClick={handleConfirmDeleteTenant} disabled={deletingTenant} sx={{ backgroundColor: "var(--danger)", "&:hover": { backgroundColor: "#da3633" } }}>
            {deletingTenant ? t("admin.tenants.deleting") : t("admin.tenants.confirmDelete")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Kullanıcı Ekleme */}
      <Dialog open={showUserModal} onClose={() => { setShowUserModal(false); resetUserForm(); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
          {t("admin.tenants.users.addTitle", { name: currentTenant.name })}
          <IconButton size="small" onClick={() => { setShowUserModal(false); resetUserForm(); }} sx={{ color: "#666" }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField autoFocus margin="dense" label={t("admin.tenants.users.fullNameLabel")} fullWidth variant="outlined" value={userForm.full_name} onChange={(e) => setUserForm({ ...userForm, full_name: e.target.value })} sx={{ mb: 2 }} />
          <TextField margin="dense" label={t("admin.tenants.users.emailLabel")} type="email" fullWidth variant="outlined" value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} sx={{ mb: 2 }} />
          <TextField margin="dense" label={t("admin.tenants.users.passwordLabel")} type="password" fullWidth variant="outlined" value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })} sx={{ mb: 2 }} />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{t("admin.tenants.users.role")}</InputLabel>
            <Select value={userForm.tenant_role} label={t("admin.tenants.users.role")} onChange={(e) => setUserForm({ ...userForm, tenant_role: e.target.value })}>
              <MenuItem value="admin">{t("admin.tenants.roles.admin")}</MenuItem>
              <MenuItem value="operatör">{t("admin.tenants.roles.operator")}</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" onClick={handleCreateUser} disabled={submittingUser} sx={{ backgroundColor: "var(--success)", "&:hover": { backgroundColor: "#2ea043" } }}>
            {submittingUser ? t("admin.tenants.saving") : t("admin.tenants.save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Kullanıcı Düzenleme */}
      <Dialog open={showEditUserModal} onClose={() => { setShowEditUserModal(false); setEditingUser(null); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
          {t("admin.tenants.users.editTitle")}
          <IconButton size="small" onClick={() => { setShowEditUserModal(false); setEditingUser(null); }} sx={{ color: "#666" }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField autoFocus margin="dense" label={t("admin.tenants.users.fullNameLabel")} fullWidth variant="outlined" value={editUserForm.full_name} onChange={(e) => setEditUserForm({ ...editUserForm, full_name: e.target.value })} sx={{ mb: 2 }} />
          <TextField margin="dense" label={t("admin.tenants.users.emailLabel")} type="email" fullWidth variant="outlined" value={editUserForm.email} onChange={(e) => setEditUserForm({ ...editUserForm, email: e.target.value })} sx={{ mb: 2 }} />
          <TextField margin="dense" label={t("admin.tenants.users.newPassword")} type="password" fullWidth variant="outlined" value={editUserForm.password} onChange={(e) => setEditUserForm({ ...editUserForm, password: e.target.value })} sx={{ mb: 2 }} placeholder={t("admin.tenants.users.newPasswordHint")} />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{t("admin.tenants.users.role")}</InputLabel>
            <Select value={editUserForm.tenant_role} label={t("admin.tenants.users.role")} onChange={(e) => setEditUserForm({ ...editUserForm, tenant_role: e.target.value })}>
              <MenuItem value="admin">{t("admin.tenants.roles.admin")}</MenuItem>
              <MenuItem value="operatör">{t("admin.tenants.roles.operator")}</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" onClick={handleUpdateUser} disabled={submittingUser} sx={{ backgroundColor: "var(--success)", "&:hover": { backgroundColor: "#2ea043" } }}>
            {submittingUser ? t("admin.tenants.updating") : t("admin.tenants.update")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Kullanıcı Silme */}
      <Dialog open={showDeleteUserDialog} onClose={() => !deletingUserLoading && setShowDeleteUserDialog(false)} maxWidth="xs" fullWidth
        slotProps={{ paper: { sx: { backgroundColor: "var(--bg-card)", border: "1px solid var(--danger)", borderRadius: "8px" } } }}
      >
        <DialogTitle sx={{ color: "var(--danger)", fontWeight: 600, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}><DeleteIcon />{t("admin.tenants.users.deleteTitle")}</Box>
          <IconButton size="small" onClick={() => setShowDeleteUserDialog(false)} disabled={deletingUserLoading} sx={{ color: "var(--text-muted)" }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "var(--text-primary)" }}>
            <Trans i18nKey="admin.tenants.users.deleteQuestion" values={{ name: deletingUser?.full_name }} components={{ b: <strong /> }} />
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" onClick={handleConfirmDeleteUser} disabled={deletingUserLoading} sx={{ backgroundColor: "var(--danger)", "&:hover": { backgroundColor: "#da3633" } }}>
            {deletingUserLoading ? t("admin.tenants.deleting") : t("admin.tenants.confirmDelete")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Lokasyon Ekleme */}
      <Dialog open={showLocationModal} onClose={() => { setShowLocationModal(false); resetLocationForm(); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
          {t("admin.tenants.loc.addTitle", { name: currentTenant.name })}
          <IconButton size="small" onClick={() => { setShowLocationModal(false); resetLocationForm(); }} sx={{ color: "#666" }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField autoFocus margin="dense" label={t("admin.tenants.loc.nameLabel")} fullWidth variant="outlined" value={locationForm.name} onChange={(e) => setLocationForm({ ...locationForm, name: e.target.value })} sx={{ mb: 2, mt: 1 }} />
          <TextField margin="dense" label={t("admin.tenants.loc.address")} fullWidth variant="outlined" multiline rows={3} value={locationForm.address} onChange={(e) => setLocationForm({ ...locationForm, address: e.target.value })} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" onClick={handleCreateLocation} disabled={submittingLocation} sx={{ backgroundColor: "var(--success)", "&:hover": { backgroundColor: "#2ea043" } }}>
            {submittingLocation ? t("admin.tenants.saving") : t("admin.tenants.save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Lokasyon Düzenleme */}
      <Dialog open={showEditLocationModal} onClose={() => { setShowEditLocationModal(false); setEditingLocation(null); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
          {t("admin.tenants.loc.editTitle")}
          <IconButton size="small" onClick={() => { setShowEditLocationModal(false); setEditingLocation(null); }} sx={{ color: "#666" }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField autoFocus margin="dense" label={t("admin.tenants.loc.nameLabel")} fullWidth variant="outlined" value={editLocationForm.name} onChange={(e) => setEditLocationForm({ ...editLocationForm, name: e.target.value })} sx={{ mb: 2, mt: 1 }} />
          <TextField margin="dense" label={t("admin.tenants.loc.address")} fullWidth variant="outlined" multiline rows={3} value={editLocationForm.address} onChange={(e) => setEditLocationForm({ ...editLocationForm, address: e.target.value })} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" onClick={handleUpdateLocation} disabled={submittingLocation} sx={{ backgroundColor: "var(--success)", "&:hover": { backgroundColor: "#2ea043" } }}>
            {submittingLocation ? t("admin.tenants.updating") : t("admin.tenants.update")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Lokasyon Silme */}
      <Dialog open={showDeleteLocationDialog} onClose={() => !deletingLocationLoading && setShowDeleteLocationDialog(false)} maxWidth="xs" fullWidth
        slotProps={{ paper: { sx: { backgroundColor: "var(--bg-card)", border: "1px solid var(--danger)", borderRadius: "8px" } } }}
      >
        <DialogTitle sx={{ color: "var(--danger)", fontWeight: 600, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}><DeleteIcon />{t("admin.tenants.loc.deleteTitle")}</Box>
          <IconButton size="small" onClick={() => setShowDeleteLocationDialog(false)} disabled={deletingLocationLoading} sx={{ color: "var(--text-muted)" }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "var(--text-primary)" }}>
            <Trans i18nKey="admin.tenants.loc.deleteQuestion" values={{ name: deletingLocation?.name }} components={{ b: <strong /> }} />
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" onClick={handleConfirmDeleteLocation} disabled={deletingLocationLoading} sx={{ backgroundColor: "var(--danger)", "&:hover": { backgroundColor: "#da3633" } }}>
            {deletingLocationLoading ? t("admin.tenants.deleting") : t("admin.tenants.confirmDelete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default function TenantList() {
  const { t } = useTranslation();
  const accessToken = useSelector((s) => s.session?.accessToken);
  const me = useSelector((s) => s.session?.me);

  // Server-side pagination state (root/superadmin picker)
  const [tenants, setTenants] = useState([]);
  const [tenantsTotal, setTenantsTotal] = useState(0);
  const [tenantsOffset, setTenantsOffset] = useState(0);
  const [tenantsLimit] = useState(20);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({ name: "", contact_person: "", parent_tenant_id: "", products: "iot" });

  // { id -> son bilinen tam tenant kaydı }, herhangi bir listeden gelen
  // satırlar biriktirilir; breadcrumb'a geri dönüldüğünde "yeniden fetch"
  // yerine bu cache'den okunur (ponytail: yeni bir tekil-tenant API çağrısı
  // eklemek yerine zaten elimizdeki liste verisini yeniden kullanıyoruz;
  // gerçek bir canlı re-fetch gerekirse getTenantById eklenebilir).
  const [tenantDetails, setTenantDetails] = useState({});
  const cacheTenants = (items) => {
    if (!items || items.length === 0) return;
    setTenantDetails((prev) => {
      const next = { ...prev };
      items.forEach((t) => { next[t.id] = t; });
      return next;
    });
  };

  // Şu an içinde bulunulan tenant zinciri (breadcrumb). Boşsa superadmin
  // için kök liste görünür; non-superadmin için hiç boş kalmaz.
  const [tenantPath, setTenantPath] = useState([]);

  const fetchTenants = async (loadMore = false) => {
    if (!accessToken) {
      setLoading(false);
      return;
    }
    if (loadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setTenantsOffset(0);
    }
    try {
      const currentOffset = loadMore ? tenantsOffset : 0;
      const data = await getTenants(accessToken, { limit: tenantsLimit, offset: currentOffset });
      const items = data.items || [];
      const total = data.total || 0;
      if (loadMore) {
        setTenants((prev) => [...prev, ...items]);
      } else {
        setTenants(items);
      }
      cacheTenants(items);
      setTenantsTotal(total);
      setTenantsOffset(currentOffset + items.length);
    } catch (err) {
      console.error("Tenant listesi alınamadı:", err);
      toast.error(t("admin.tenants.root.listFailed"));
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchTenants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  // Tenant admin ise: seçim ekranı atlanır, kendi tenant'ı doğrudan açılır.
  useEffect(() => {
    if (me?.global_role !== "superadmin" && me?.tenant_id && tenantPath.length === 0) {
      const own = tenants.find((t) => t.id === me.tenant_id);
      if (own) setTenantPath([{ id: own.id, name: own.name }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenants, me]);

  const resetForm = () => setFormData({ name: "", contact_person: "", parent_tenant_id: "", products: "iot" });

  // Kök tenant sayısı: sadece o an yüklü sayfadaki tenant'lardan (server-side
  // pagination ile paralel) hesaplanır — cheap, ek API çağrısı yok.
  const { childrenById, rootTenants } = useMemo(() => {
    const ids = new Set(tenants.map((t) => t.id));
    const byParent = {};
    const roots = [];
    for (const t of tenants) {
      if (t.parent_tenant_id && ids.has(t.parent_tenant_id)) {
        if (!byParent[t.parent_tenant_id]) byParent[t.parent_tenant_id] = [];
        byParent[t.parent_tenant_id].push(t);
      } else {
        roots.push(t);
      }
    }
    return { childrenById: byParent, rootTenants: roots };
  }, [tenants]);

  const handleCreateTenant = async () => {
    if (!formData.name.trim()) {
      toast.error(t("admin.tenants.sub.nameEmpty"));
      return;
    }
    setSubmitting(true);
    try {
      const { products, ...rest } = formData;
      const picked = PRODUCT_CHOICES.find((c) => c.value === products);
      await createTenant(accessToken, { ...rest, parent_tenant_id: formData.parent_tenant_id || null, ...(products !== "iot" && picked ? { products: picked.products } : {}) });
      toast.success(t("admin.tenants.root.created"));
      setShowAddModal(false);
      resetForm();
      fetchTenants();
    } catch (err) {
      console.error("Tenant oluşturulamadı:", err);
      toast.error(err?.response?.data?.detail || t("admin.tenants.root.createFailed"));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Tenant workspace (tabbed) view
  if (tenantPath.length > 0) {
    return (
      <TenantWorkspace
        key={tenantPath[tenantPath.length - 1].id}
        tenantPath={tenantPath}
        setTenantPath={setTenantPath}
        accessToken={accessToken}
        me={me}
        tenantDetails={tenantDetails}
        onTenantsFetched={cacheTenants}
        onExitToRoot={() => {
          if (me?.global_role === "superadmin") {
            setTenantPath([]);
            fetchTenants();
          }
        }}
      />
    );
  }

  // Non-superadmin: kendi tenant'ı henüz çözümlenmediyse bekleniyor demektir.
  if (me?.global_role !== "superadmin") {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Superadmin kök liste (flat, nested accordion yok)
  return (
    <Box sx={{ color: "var(--text-primary)" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: "bold", color: "var(--text-primary)" }}>{t("admin.tenants.title")}</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setShowAddModal(true)}
          sx={{ backgroundColor: "var(--success)", "&:hover": { backgroundColor: "#2ea043" } }}
        >
          {t("admin.tenants.root.new")}
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{ backgroundColor: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: "8px" }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "var(--bg-hover)" }}>
              <TableCell sx={{ color: "var(--text-muted)", fontWeight: 600 }}>{t("admin.tenants.root.company")}</TableCell>
              <TableCell sx={{ color: "var(--text-muted)", fontWeight: 600 }}>{t("admin.tenants.root.contact")}</TableCell>
              <TableCell sx={{ color: "var(--text-muted)", fontWeight: 600 }}>{t("admin.tenants.root.subCount")}</TableCell>
              <TableCell sx={{ color: "var(--text-muted)", fontWeight: 600 }} align="center">{t("admin.tenants.root.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tenants.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} sx={{ color: "var(--text-muted)", textAlign: "center", py: 4 }}>
                  {t("admin.tenants.root.empty")}
                </TableCell>
              </TableRow>
            ) : (
              rootTenants.map((tenant) => (
                <TableRow key={tenant.id} sx={{ "&:hover": { backgroundColor: "var(--bg-hover)" } }}>
                  <TableCell sx={{ fontWeight: 600 }}>{tenant.name}</TableCell>
                  <TableCell>{tenant.contact_person || "-"}</TableCell>
                  <TableCell>{childrenById[tenant.id]?.length ?? 0}</TableCell>
                  <TableCell align="center">
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setTenantPath([{ id: tenant.id, name: tenant.name }])}
                      sx={{ color: "var(--accent-navy-light)", borderColor: "var(--border)" }}
                    >
                      {t("admin.tenants.root.manage")}
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {tenants.length > 0 && tenants.length < tenantsTotal && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <Button
            variant="outlined"
            onClick={() => fetchTenants(true)}
            disabled={loadingMore}
            sx={{ color: "var(--text-muted)", borderColor: "var(--border)", "&:hover": { borderColor: "var(--text-muted)" } }}
          >
            {loadingMore ? t("admin.tenants.root.loadingMore") : t("admin.tenants.root.loadMore", { loaded: tenants.length, total: tenantsTotal })}
          </Button>
        </Box>
      )}

      {/* Yeni (Kök) Tenant Ekleme Modal */}
      <Dialog open={showAddModal} onClose={() => { setShowAddModal(false); resetForm(); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {t("admin.tenants.root.addTitle")}
          <IconButton size="small" onClick={() => { setShowAddModal(false); resetForm(); }} sx={{ color: "#666" }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField autoFocus margin="dense" label={t("admin.tenants.sub.companyLabel")} fullWidth variant="outlined" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} sx={{ mb: 2 }} />
          <TextField margin="dense" label={t("admin.tenants.root.contact")} fullWidth variant="outlined" value={formData.contact_person} onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })} sx={{ mb: 2 }} />
          <FormControl fullWidth margin="dense">
            <InputLabel>{t("admin.tenants.root.parent")}</InputLabel>
            <Select value={formData.parent_tenant_id} label={t("admin.tenants.root.parent")} onChange={(e) => setFormData({ ...formData, parent_tenant_id: e.target.value })}>
              <MenuItem value="">{t("admin.tenants.root.noParent")}</MenuItem>
              {tenants.map((tn) => (
                <MenuItem key={tn.id} value={tn.id}>{tn.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel>{t("admin.products.title")}</InputLabel>
            <Select value={formData.products} label={t("admin.products.title")} onChange={(e) => setFormData({ ...formData, products: e.target.value })}>
              {PRODUCT_CHOICES.map((c) => (
                <MenuItem key={c.value} value={c.value}>{t(`admin.products.${c.value}`)}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" onClick={handleCreateTenant} disabled={submitting} sx={{ backgroundColor: "var(--success)", "&:hover": { backgroundColor: "#2ea043" } }}>
            {submitting ? t("admin.tenants.saving") : t("admin.tenants.save")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
